/**
 * VehicleResultPage Shortlist Management Mixin
 *
 * This mixin is SPECIFIC to VehicleResultPage component only.
 * Provides methods for managing shortlist operations.
 *
 * All methods are prefixed with "vrpShortlist_" to indicate their source.
 *
 * Methods provided:
 * - vrpShortlist_handleRemoveShortlist: Remove vehicle from shortlist
 * - vrpShortlist_handleInput: Handle vehicle shortlist input (add/remove)
 * - vrpShortlist_isVehicleShortlisted: Check if vehicle is in shortlist
 * - vrpShortlist_isShortlistAtMax: Check if shortlist has reached max capacity
 */
import {
  GTM_EVENT_NAME_SME_LEASE_CALCULATOR_CAR,
  GTM_EVENT_STAGE_ADD_TO_SHORTLIST,
  GTM_EVENT_VALUES_LEASE_CALCULATOR_CAR
} from '../../utils/constants';
import { CalculatorDialog } from '../../components/Calculator/CalculatorDialog';
import { evaCloseCircleOutline } from '@quasar/extras/eva-icons';
import { formatWordsAsSentences } from '../../utils/calculator/wordFormatter';
import { formatVehicleDescription } from '../../utils/calculator/vehicleFormatter';

export default {
  methods: {
    /**
     * Remove vehicle from shortlist and update database
     * @param {Object} value - Vehicle to remove
     */
    async vrpShortlist_handleRemoveShortlist(value) {
      const { applicationId } = this.vehicleApplication;
      const { id: userId } = this.user;
      this.shortlist = this.shortlist.filter((vehicle) => vehicle.catalogVehicleId !== value.catalogVehicleId);
      await this.updateShortListDB(applicationId, userId);
    },

    /**
     * Check if vehicle is already in shortlist
     * @param {Object} vehicle - Vehicle to check
     * @returns {Boolean}
     */
    vrpShortlist_isVehicleShortlisted(vehicle) {
      return this.shortlist.some((shortlist) => shortlist.catalogVehicleId === vehicle.catalogVehicleId);
    },

    /**
     * Check if shortlist has reached maximum capacity (3 vehicles)
     * @returns {Boolean}
     */
    vrpShortlist_isShortlistAtMax() {
      const MAX_SHORTLIST_COUNT = 3;
      return this.shortlist.length >= MAX_SHORTLIST_COUNT;
    },

    /**
     * Handle vehicle shortlist input (add/remove from shortlist)
     * Manages shortlist state, GTM tracking, and notifications
     * @param {Object} value - Vehicle with isShortlisted property
     */
    vrpShortlist_handleInput(value) {
      if (value.isShortlisted && this.shortlist.length > 1) {
        this.disableShortlist = true;
        this.showShortlistedVehicles = true;
      } else if (!value.isShortlisted) {
        this.disableShortlist = false;
        this.shortlist = this.shortlist.filter((option) => option.catalogVehicleId != value.catalogVehicleId);
      }

      if (value.isShortlisted && this.shortlist.length == 0) {
        this.showShortlistedVehicles = false;
      }

      if (value.isShortlisted) {
        this.shortlist.push(value);

        const model = `${formatWordsAsSentences(value.vehicleModel)} ${value.vehicleTrim || ''} |  ${value.vehicleDrivetrain}`;
        const variant = formatVehicleDescription(value)?.trim();

        setTimeout(() => {
          this.$gtm.trackEvent({
            event: GTM_EVENT_NAME_SME_LEASE_CALCULATOR_CAR,
            stage: GTM_EVENT_STAGE_ADD_TO_SHORTLIST,
            data: {
              ...GTM_EVENT_VALUES_LEASE_CALCULATOR_CAR,
              ...(value?.vehicleMake?.length && { make: value?.vehicleMake }),
              ...(value?.vehicleFueltype?.length && { fuel: value?.vehicleFueltype }),
              ...(value?.vehicleBodytype?.length && { type: value?.vehicleBodytype }),
              ...(model?.length && { model }),
              ...(variant?.length && { variant }),
              ...(this.repayment?.length && { pay_cycle: this.repayment }),
              ...(value?.consumerPrice && { purchase_price: value?.consumerPrice })
            }
          });
        });
      }

      if (this.vrpShortlist_isShortlistAtMax()) {
        this.$euiNotifications.emit({
          notification: CalculatorDialog,
          headerIcon: evaCloseCircleOutline,
          header: 'Limit has been reached.'
        });
      }

      if (this.user === null || this.user === undefined) {
        this.updateShortList();
        return;
      }
      const { applicationId } = this.vehicleApplication;
      const { id: userId } = this.user;
      this.updateShortListDB(applicationId, userId);
    }
  }
};
